import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { ChangeEvent, useEffect, useState } from "react";
import { usePosterCreate, usePosterGetFile, usePosterUpdate } from "@/lib/api/poster";
import { Indeterminate } from "../atoms/Indeterminate";
import { Poster } from "@/lib/types/poster";
import { FileImg } from "../atoms/FileImg";
import { toast } from "sonner";
import { isA4AspectRatio } from "@/lib/utils/utils";

interface Props {
  poster: Pick<Poster, "id" | "eventId" | "scc">;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function EventPosterDialog({ poster, open, setOpen }: Props) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  const { data: initialFile, isLoading } = usePosterGetFile(poster, true)
  const [file, setFile] = useState<File | undefined>(initialFile)

  useEffect(() => {
    setError("")

    if (open) {
      setFile(initialFile)
    } else {
      setFile(undefined)
    }
  }, [open, initialFile])

  const posterCreate = usePosterCreate()
  const posterUpdate = usePosterUpdate()

  const handleSubmit = () => {
    if (!file) {
      return
    }

    setSubmitting(true)

    let action
    let message
    if (poster.id) {
      action = posterUpdate
      message = "updated"
    } else {
      action = posterCreate
      message = "created"
    }

    action.mutate({ poster, file }, {
      onSuccess: () => {
        toast.success(`Poster ${message}`)
        setOpen(false)
      },
      onError: (error: Error) => toast.error("Failed", { description: error.message }),
      onSettled: () => setSubmitting(false)
    })
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setError(() => "")

    const file = e.target.files?.[0] ?? undefined

    if (!file) {
      setFile(undefined)
      return
    }

    isA4AspectRatio(file)
      .then((isA4) => {
        if (!isA4) {
          setFile(undefined)
          setError(() => "Poster needs to have the same aspect ratio as A4")
          return
        }

        setFile(file)
      })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogTitle>
          Edit event poster
        </DialogTitle>
        <DialogDescription asChild>
          <div className="flex flex-col gap-8">
            <div className="space-y-3">
              <Label htmlFor="picture-scc">Poster</Label>
              {!isLoading ? (
                <>
                  <div className="flex items-center gap-1">
                    <Input id="picture-scc" type="file" accept=".png" onChange={handleChange} />
                  </div>
                  {error && (
                    <div>
                      <span className="text-red-500">{error}</span>
                    </div>
                  )}
                  {file && (
                    <div className="w-fit rounded-xl overflow-hidden mx-auto">
                      <FileImg file={file} isLoading={isLoading} alt="Poster preview" className="max-h-[60vh] w-auto" />
                    </div>
                  )}
                </>
              ) : (
                <Indeterminate />
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSubmit} disabled={!file || submitting}>
                Submit
              </Button>
            </div>
          </div>
        </DialogDescription>
      </DialogContent>
    </Dialog>
  )
}
